import React from 'react'
import { useIntl } from 'react-intl'
import styled, { css } from 'styled-components'
import { Maybe } from '../../common/types'
import { JobItemInput } from './types'
import { Currency } from '../Currency'
import { calculateOrderTotals } from './calculateOrderTotals'
import { messages } from './messages'
import { formatMessageSafe } from '../../util/translateInputOptions'

interface Props {
  jobItems: JobItemInput[]
  itemsIncludeVat: boolean
  discountItem: Maybe<JobItemInput>
}

export { getItemsTotal } from './calculateOrderTotals'

export const Totals: React.FC<Props> = ({ itemsIncludeVat, jobItems, discountItem }) => {
  const intl = useIntl()
  const formatMessage = formatMessageSafe(intl)

  const totals = calculateOrderTotals({ itemsIncludeVat, jobItems, discountItem })
  if (!totals) return null

  const { discountNetTotal, discountGrossTotal, netAmountWithDiscountItem, vatAmountWithDiscountItem, grossAmountWithDiscountItem, hasUndefinedPrice } = totals

  return (
    <TotalsLayout>
      {discountNetTotal !== 0 && (
        <>
          <Description>{formatMessage(messages.discountNetGross)}</Description>
          <Sum>
            <Currency>{discountNetTotal}</Currency>
            {' / '}
            <Currency>{discountGrossTotal}</Currency>
          </Sum>
        </>
      )}

      <Description>{formatMessage(messages.netAmount)}</Description>
      <Sum>{hasUndefinedPrice ? 'N/A' : <Currency>{netAmountWithDiscountItem}</Currency>}</Sum>

      <Description>{formatMessage(messages.vat)}</Description>
      <Sum>{hasUndefinedPrice ? 'N/A' : <Currency>{vatAmountWithDiscountItem}</Currency>}</Sum>

      <Description bold>{formatMessage(messages.total)}</Description>
      <Sum bold>{hasUndefinedPrice ? 'N/A' : <Currency>{grossAmountWithDiscountItem}</Currency>}</Sum>
    </TotalsLayout>
  )
}

const TotalsLayout = styled.div`
  padding: 0;
  margin: 0 0 1rem 0;
  display: grid;
  grid-gap: 0.5rem;
  grid-template-columns: 5fr minmax(200px, 1fr);
  @media (max-width: ${props => props.theme.screenSize.mobile}) {
    grid-template-columns: 1fr;
    grid-gap: 0;
  }
`

interface DescriptionProps {
  bold?: boolean
}

const Description = styled.div<DescriptionProps>`
  color: ${props => props.theme.colors.black}
  text-align: right;
  ${props =>
    props.bold &&
    css`
      font-weight: 600;
    `};
  @media (max-width: ${props => props.theme.screenSize.mobile}) {
    margin: 0.75rem 0 0.25rem 0;
    font-weight: 600;
  }
`

interface SumProps {
  bold?: boolean
}

const Sum = styled.div<SumProps>`
  color: ${props => props.theme.colors.black}
  text-align: right;
  font-weight: ${props => props.bold && props.theme.fontWeight.bold};
  @media (max-width: ${props => props.theme.screenSize.mobile}) {
    font-weight: ${props => props.bold && props.theme.fontWeight.normal};
  }
`
